import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { LeadService } from 'src/app/shared/store/lead/lead.service';
import {
  PushLeadModel,
  LeadDetailModel,
  InsuranceCategory,
} from 'src/app/shared/store/lead/types';
import { SubHeaderService } from 'src/app/shared/store/subheader/subheader.service';
import { BreadcrumbService } from 'xng-breadcrumb';
import { filter } from 'rxjs/operators';
import { SmartAction, View } from '../../../../shared/store/view/types';
import { ViewService } from '../../../../shared/store/view/views.service';
import { CategoryChoiceComponent } from 'src/app/shared/components/category-choice/category-choice.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SalesType } from '../../../../shared/enum/sales-type';
import { filterTrue } from 'src/app/shared/pipe/rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-lead-details',
  templateUrl: './lead-details.component.html',
  styleUrls: ['./lead-details.component.scss'],
  standalone: false,
})
export class LeadDetailsComponent implements OnInit, OnDestroy {
  leadUniqueId: string;
  view$: Observable<View>;
  leadDetail$: Observable<LeadDetailModel>;
  active = '';
  category: number;
  fragment: string;
  smartActions: SmartAction[];
  deleteResult$: Observable<boolean>;
  setAsPrincipalResult$: Observable<boolean>;
  markAsNotDuplicateResult$: Observable<boolean>;

  constructor(
    private readonly leadService: LeadService,
    private route: ActivatedRoute,
    private readonly router: Router,
    private readonly subHeaderService: SubHeaderService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
    private readonly modalService: NgbModal,
  ) {
    this.deleteResult$ = this.leadService.delete.value$;
    this.setAsPrincipalResult$ = this.leadService.setAsPrincipal.value$;
    this.markAsNotDuplicateResult$ = this.leadService.markAsNotDuplicate.value$;
  }

  ngOnInit(): void {
    this.view$ = this.viewService.getViews.value$;
    this.leadDetail$ = this.leadService.getByUniqueId.value$;

    // Details/A → Details/B reuses this component: resetting first empties the @if, so every child
    // (comments, tabs) is destroyed and recreated on B instead of keeping what it loaded for A.
    this.route.params.pipe(untilDestroyed(this)).subscribe((val) => {
      this.leadUniqueId = val['uniqueId'];
      this.resetLead();
      this.leadService.getByUniqueId.call(this.leadUniqueId);
    });

    this.fragment = this.route.snapshot.fragment!;

    this.smartActions = [
      {
        id: 'contracts',
        label: 'Contracts',
        url: '/Insurance/Contracts/List',
        count: 0,
        filter: {
          domains: [
            {
              field: 'LeadId',
              operator: {
                id: 'EqualTo',
                label: '',
                numberOfValues: 1,
              },
              connector: 'Or',
              fieldLabel: '',
              text: '',
              isActive: true,
              text2: '',
              value: '',
              value2: null,
            },
          ],
          name: '',
          tag: 'SmartAction',
          text: '',
          description: '',
          isReadOnly: false,
        },
        icon: 'file-check-outline',
        actif: true,
        singleId: '',
      },
    ];

    this.leadDetail$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@leadDetails', x.name);
        switch (x.productCategoryProductCategoryEnumKey) {
          case InsuranceCategory.Health:
            this.leadService.getHealthDetail.call(x.id.toString());
            break;
          case InsuranceCategory.Car:
          case InsuranceCategory.Bike:
            this.leadService.getCarDetail.call(x.id.toString());
            break;
          case InsuranceCategory.House:
            this.leadService.getHouseDetail.call(x.id.toString());
            break;
          default:
            break;
        }

        if (x.contracts) {
          this.smartActions[0].filter.domains[0].value = x.id.toString();
          this.smartActions[0].filter.name = x.name;
          this.smartActions[0].filter.text = x.name;
          this.smartActions[0].count = x.contracts.length;
          this.smartActions[0].label =
            x.contracts.length > 1
              ? 'SMARTACTIONS_LBL_CONTRACTS'
              : 'SMARTACTIONS_LBL_CONTRACT';
          this.smartActions[0].singleId =
            x.contracts.length === 1 ? x.contracts[0].uniqueId : '';
        }
      });

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);
      });

    this.subHeaderService
      .getTriggerAction()
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        let postLeadModel: PushLeadModel = {
          id: this.leadUniqueId,
          oggo: false,
          smartbroker: false,
        };
        switch (x) {
          case 'leadSendOggo':
            postLeadModel.oggo = true;
            this.leadService.sendLead.call(postLeadModel);
            break;
          case 'leadSendSmartbroker':
            postLeadModel.smartbroker = true;
            this.leadService.sendLead.call(postLeadModel);
            break;
          case 'leadEdit':
            this.router.navigate(['/Crm/Leads/Edit', this.leadUniqueId]);
            break;
          case 'leadDelete':
            this.onDelete();

            break;
          case 'leadCreate':
            this.createLead();
            break;
          case 'leadDuplicatesSetAsPrincipal':
            this.setAsPrincipal();
            break;
          case 'leadDuplicatesMarkAsNotDuplicate':
            this.markAsNotDuplicate();
            break;
          default:
            break;
        }
        this.subHeaderService.setTriggerAction('');
      });

    // One subscription per call for the component's lifetime, not one per click: a failed call
    // never emits true, so a per-click take(1) would stay pending and pile up until the next success.
    this.deleteResult$
      .pipe(filterTrue(), untilDestroyed(this))
      .subscribe(() => {
        this.leadService.delete.reset();
        this.router.navigate(['/Crm/Leads/List']);
      });

    this.setAsPrincipalResult$
      .pipe(filterTrue(), untilDestroyed(this))
      .subscribe(() => {
        this.leadService.setAsPrincipal.reset();
      });

    this.markAsNotDuplicateResult$
      .pipe(filterTrue(), untilDestroyed(this))
      .subscribe(() => {
        this.leadService.markAsNotDuplicate.reset();
      });
  }

  ngOnDestroy() {
    this.resetLead();
    this.breadcrumbService.set('@leadDetails', ' ');
  }

  private resetLead() {
    this.leadService.getByUniqueId.reset();
    this.leadService.getHealthDetail.reset();
    this.leadService.getCarDetail.reset();
    this.leadService.getHouseDetail.reset();
  }

  createLead() {
    const modalRef = this.modalService.open(CategoryChoiceComponent);
    modalRef.componentInstance.salesType = SalesType.LEAD;
  }

  onDelete() {
    this.leadService.delete.call(this.leadUniqueId.toString());
  }

  private setAsPrincipal() {
    this.leadService.setAsPrincipal.call(this.leadUniqueId);
  }

  private markAsNotDuplicate() {
    this.leadService.markAsNotDuplicate.call(this.leadUniqueId);
  }
}
